import React, { Suspense, lazy, useEffect } from 'react';
import { Route, Switch, useLocation } from 'react-router-dom';
import LoginPage from 'split-auth/build/pages/auth/Login';
import RegisterPage from 'split-auth/build/pages/auth/Register';
import ResetPasswordPage from 'split-auth/build/pages/auth/ResetPassword';
import ResetSentPage from 'split-auth/build/pages/auth/ResetSent';

import PathLink from '../utils/pathLinkUrl/PathLink';

import Page from '../components/Page';
import LoadPage from './LoadPage';
// import { ENVIRONMENT } from '../config';

const HomePage = lazy(() => import('./Home'));

const ChatPage = lazy(() => import('./Chat'));

const PerformancePage = lazy(() => import('./Performance'));

const DataExportsPage = lazy(() => import('./DataExports'));

const CampaignsListPage = lazy(() => import('./Campaigns/routes/CampaignsList'));
const CampaignCreatePage = lazy(() => import('./Campaigns/routes/CampaignCreate'));
const CampaignEditPage = lazy(() => import('./Campaigns/routes/CampaignEdit'));
const CampaignSummaryPage = lazy(() => import('./Campaigns/routes/CampaignSummary'));

const PromotionsPage = lazy(() => import('./Promotions/routes/PromotionsList'));
const PromotionCreatePage = lazy(() => import('./Promotions/routes/PromotionCreate'));
const PromotionEditPage = lazy(() => import('./Promotions/routes/PromotionEdit'));

const DiscoverPage = lazy(() => import('./Discover'));
const CreatorPage = lazy(() => import('./Creator'));
const SimilarCreatorsPage = lazy(() => import('./SimilarCreators'));

const MyCreatorsPage = lazy(() => import('./MyCreators/routes/MyCreatorsList'));
const ManageGroupPage = lazy(() => import('./MyCreators/routes/ManageGroup'));
const ManageSmartGroupPage = lazy(() => import('./MyCreators/routes/ManageSmartGroup'));

const ManageLookbookPage = lazy(() => import('./ManageLookbook'));
const LookbookSummaryPage = lazy(() => import('./LookbookSummary'));

const ManageReimbursementPage = lazy(() => import('./ManageReimbursement'));
const ManageCodePage = lazy(() => import('./ManageCode'));
const GiftsPage = lazy(() => import('./Gifts/GiftsList'));
const DiscountsPage = lazy(() => import('./Discounts/DiscountsList'));

const BillingPage = lazy(() => import('./Billing'));

const ReferralsPage = lazy(() => import('./Refer'));

const AffiliateNetworkPage = lazy(() => import('./AffiliateNetwork'));

const MerchantsPage = lazy(() => import('./Merchants'));
const AddMerchantPage = lazy(() => import('./MerchantsAdd'));
const EditMerchantPage = lazy(() => import('./MerchantsEdit'));

const MembersPage = lazy(() => import('./Members'));
const InvitationsPage = lazy(() => import('./Invitations'));
const MemberInvitePage = lazy(() => import('./MemberInvite'));

const OrganizationPage = lazy(() => import('./Organization'));
const OrganizationEditContactPage = lazy(() => import('./OrganizationEditContact'));

const AccountPage = lazy(() => import('./Account'));
const AccountNameEditPage = lazy(() => import('./AccountNameEdit'));
const AccountEmailEditPage = lazy(() => import('./AccountEmailEdit'));
const AccountPhoneEditPage = lazy(() => import('./AccountPhoneEdit'));
const AccountPhoneVerification = lazy(() => import('./AccountPhoneVerification'));

const HelpPage = lazy(() => import('./Help'));
const SecretSettingsPage = lazy(() => import('./SecretSettings'));
const AcceptInvitationPage = lazy(() => import('./AcceptInvitation'));
const NotFound = lazy(() => import('./NotFound'));

export const routes = {
  root: new PathLink('/'),
  login: {
    business: new PathLink('/business/signin')
  },
  register: {
    business: new PathLink('/business/signup')
  },
  passwordReset: {
    business: new PathLink('/business/password-reset')
  },
  passwordReset_sent: {
    business: new PathLink('/business/password-reset-sent')
  },
  acceptInvitation: {
    business: new PathLink('/business/accept-invitation')
  },
  chat: {
    root: new PathLink('/chat')
  },
  performance: new PathLink('/performance'),
  dataExports: new PathLink('/data-exports'),
  campaigns: {
    list: new PathLink('/bonuses'),
    create: new PathLink('/bonuses/create'),
    edit: new PathLink('/bonuses/edit/:id', id => `/bonuses/edit/${id}`),
    summary: new PathLink('/bonuses/:id', id => `/bonuses/${id}`)
  },
  promotions: {
    root: new PathLink('/promotions'),
    create: new PathLink('/promotions/create'),
    edit: new PathLink('/promotions/edit/:id', id => `/promotions/edit/${id}`)
  },
  discover: {
    root: new PathLink('/discover')
  },
  myCreators: {
    root: new PathLink('/my-creators'),
    manageGroup: new PathLink(
      '/my-creators/groups/:id?/regular',
      id => id ? `/my-creators/groups/${id}/regular` : '/my-creators/groups/regular'
    ),
    manageSmartGroup: new PathLink(
      '/my-creators/groups/:id?/smart',
      id => id ? `/my-creators/groups/${id}/smart` : '/my-creators/groups/smart'
    ),
    editRegularGroup: new PathLink(
      '/my-creators/groups/:id/regular',
      id => `/my-creators/groups/${id}/regular`
    ),
    editSmartGroup: new PathLink(
      '/my-creators/groups/:id/smart',
      id => `/my-creators/groups/${id}/smart`
    )
  },
  creator: {
    root: {
      public: new PathLink('/creator/:id/public', id => `/creator/${id}/public`),
      custom: new PathLink('/creator/:id/custom', id => `/creator/${id}/custom`),
      chat: new PathLink('/creator/:id/chat', id => `/creator/${id}/chat`)
    },
    similarCreators: {
      public: new PathLink('/creator/:id/public/similar-creators', id => `/creator/${id}/public/similar-creators`),
      custom: new PathLink('/creator/:id/custom/similar-creators', id => `/creator/${id}/custom/similar-creators`),
      chat: new PathLink('/creator/:id/chat/similar-creators', id => `/creator/${id}/chat/similar-creators`)
    }
  },
  billing: {
    root: new PathLink('/billing')
  },
  refer: {
    root: new PathLink('/refer')
  },
  affiliateNetwork: {
    root: new PathLink('/affiliate-network'),
    tab: new PathLink(
      '/affiliate-network/:tab?',
      tab => tab ? `/affiliate-network/${tab}` : '/affiliate-network'
    )
  },
  merchants: {
    root: new PathLink('/merchants'),
    add: new PathLink('/merchants/add'),
    edit: new PathLink(
      '/merchants/edit/:id',
      id => `/merchants/edit/${id}`
    )
  },
  members: {
    list: new PathLink('/members/list'),
    invite: new PathLink('/members/invite'),
    invitations: new PathLink('/members/invitations')
  },
  organization: {
    root: new PathLink('/organization'),
    contactDetails: new PathLink('/organization/edit/contact')
  },
  account: {
    root: new PathLink('/account'),
    nameEdit: new PathLink('/account/name-edit'),
    emailEdit: new PathLink('/account/email-edit'),
    phoneEdit: new PathLink('/account/phone-edit'),
    phoneVerify: new PathLink('/account/phone-verification')
  },
  help: new PathLink('/help'),
  SecretSettings: new PathLink('/secret-settings'),
  connections: new PathLink('/connections'),
  codes: {
    root: new PathLink('/codes'),
    create: new PathLink(
      '/codes/create/:purpose/:type',
      (purpose, type) => {
        // if (purpose !== 'gift' && purpose !== 'share') {
        //   return '/codes/create/gift/amount_off_products';
        // } else {
        //   if (type !== 'amount_off_products' && type !== 'bxgy' && type !== 'amount_off_order' && type !== 'free_shipping') {
        //     return '/codes/create/gift/amount_off_products';
        //   } else {
        //     return `/codes/create/${purpose}/${type}`;
        //   }
        // }
        return `/codes/create/${purpose}/${type}`;
      }
    ),
    edit: new PathLink(
      '/codes/edit/:id',
      id => `/codes/edit/${id}`
    ),
    duplicate: new PathLink(
      '/codes/duplicate/:id',
      id => `/codes/duplicate/${id}`
    )
  },
  gifts: {
    root: new PathLink('/gifts'),
    create: new PathLink(
      '/gifts/create/:type',
      type => `/gifts/create/${type}`
    ),
    edit: new PathLink(
      '/gifts/edit/:id',
      id => `/gifts/edit/${id}`
    ),
    duplicate: new PathLink(
      '/gifts/duplicate/:id',
      id => `/gifts/duplicate/${id}`
    )
  },
  reimbursement: {
    create: new PathLink('/reimbursement/create')
  },
  lookbook: {
    create: {
      shopify: new PathLink('/lookbook/create/shopify'),
      manual: new PathLink('/lookbook/create/manual')
    },
    edit: {
      shopify: new PathLink(
        '/lookbook/edit/shopify/:id',
        id => `/lookbook/edit/shopify/${id}`
      ),
      manual: new PathLink(
        '/lookbook/edit/manual/:id',
        id => `/lookbook/edit/manual/${id}`
      )
    },
    inventory: new PathLink(
      '/lookbook/inventory/:id',
      id => `/lookbook/inventory/${id}`
    ),
    preview: new PathLink(
      '/lookbook/preview/:id',
      id => `/lookbook/preview/${id}`
    ),
    summary: new PathLink(
      '/lookbook/summary/:id',
      id => `/lookbook/summary/${id}`
    )
  },
  discounts: {
    root: new PathLink('/discounts'),
    create: new PathLink(
      '/discounts/create/:type',
      type => `/discounts/create/${type}`
    ),
    edit: new PathLink(
      '/discounts/edit/:id',
      id => `/discounts/edit/${id}`
    ),
    duplicate: new PathLink(
      '/discounts/duplicate/:id',
      id => `/discounts/duplicate/${id}`
    )
  }
};

const Routes = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    if (pathname === routes.help.path) toggleIntercom(false);
    else toggleIntercom(true);
  }, [pathname]);  // eslint-disable-line

  const toggleIntercom = param => {
    if (window.Intercom) {
      window.Intercom('update', { 'hide_default_launcher': param });

      if (param) window.Intercom('hide');
    }
  };

  return (
    <Suspense fallback={<LoadPage />}>
      <Switch>
        <Route exact path={routes.root.path} component={HomePage} />
        <Route exact path={routes.login.business.path} component={() =>
          <Page hideMenu hideNavbar>
            <LoginPage type="business" />
          </Page>
        } />
        <Route exact path={routes.register.business.path} component={() =>
          <Page hideMenu hideNavbar>
            <RegisterPage type="business" />
          </Page>
        } />
        <Route exact path={routes.passwordReset.business.path} component={() =>
          <Page hideMenu hideNavbar>
            <ResetPasswordPage type="business" />
          </Page>
        } />
        <Route exact path={routes.passwordReset_sent.business.path} component={() =>
          <Page hideMenu hideNavbar>
            <ResetSentPage type="business" />
          </Page>
        } />

        <Route exact path={routes.acceptInvitation.business.path} component={AcceptInvitationPage}/>

        <Route exact path={routes.refer.root.path} component={ReferralsPage} />

        <Route exact path={routes.affiliateNetwork.tab.path} component={AffiliateNetworkPage} />

        <Route exact path={routes.merchants.root.path} component={MerchantsPage} />
        <Route exact path={routes.merchants.add.path} component={AddMerchantPage}/>
        <Route exact path={routes.merchants.edit.path} component={EditMerchantPage}/>

        {/*<Route exact path={routes.domains.root.path} component={DomainsPage} />
        <Route exact path={routes.domains.add.path} component={DomainAddPage} />
        <Route exact path={routes.domains.edit.path} component={DomainEditPage} />*/}

        {/*<Route exact path={routes.commissions.root.path} component={CommissionsPage} />
        <Route exact path={routes.commissions.details.path} component={CommissionsDetailsPage} />
        <Route exact path={routes.commissions.edit.path} component={CommissionsEditPage} />*/}

        <Route exact path={routes.campaigns.list.path} component={CampaignsListPage} />
        <Route exact path={routes.campaigns.create.path} component={CampaignCreatePage} />
        <Route exact path={routes.campaigns.edit.path} component={CampaignEditPage} />
        <Route exact path={routes.campaigns.summary.path} component={CampaignSummaryPage} />

        <Route exact path={routes.promotions.root.path} component={PromotionsPage} />
        <Route exact path={routes.promotions.create.path} component={PromotionCreatePage} />
        <Route exact path={routes.promotions.edit.path} component={PromotionEditPage} />

        <Route exact path={routes.myCreators.root.path} component={MyCreatorsPage} />
        <Route exact path={routes.myCreators.manageGroup.path} component={ManageGroupPage} />
        <Route exact path={routes.myCreators.manageSmartGroup.path} component={ManageSmartGroupPage} />
        <Route exact path={routes.creator.root.public.path} component={() => <CreatorPage type="public" />} />
        <Route exact path={routes.creator.root.custom.path} component={() => <CreatorPage type="custom" />} />
        <Route exact path={routes.creator.root.chat.path} component={() => <CreatorPage type="chat" />} />
        <Route exact path={routes.creator.similarCreators.public.path} component={() => <SimilarCreatorsPage type="public" />} />
        <Route exact path={routes.creator.similarCreators.custom.path} component={() => <SimilarCreatorsPage type="custom" />} />
        <Route exact path={routes.creator.similarCreators.chat.path} component={() => <SimilarCreatorsPage type="chat" />} />

        <Route exact path={routes.gifts.root.path} component={GiftsPage} />
        <Route exact path={routes.gifts.create.path} render={() => <ManageCodePage action="create" defaultPurpose="gift" />} />
        <Route exact path={routes.gifts.edit.path} render={() => <ManageCodePage action="edit" />} />
        <Route exact path={routes.gifts.duplicate.path} render={() => <ManageCodePage action="duplicate" />} />

        <Route exact path={routes.lookbook.create.manual.path} render={() => <ManageLookbookPage action="create" type="manual" />} />
        <Route exact path={routes.lookbook.edit.manual.path} render={() => <ManageLookbookPage action="edit" type="manual" />} />
        <Route exact path={routes.lookbook.create.shopify.path} render={() => <ManageLookbookPage action="create" type="shopify" />} />
        <Route exact path={routes.lookbook.edit.shopify.path} render={() => <ManageLookbookPage action="edit" type="shopify" />} />
        <Route exact path={routes.lookbook.inventory.path} render={() => <ManageLookbookPage action="inventory" />} />
        <Route exact path={routes.lookbook.preview.path} render={() => <ManageLookbookPage action="preview" />} />
        <Route exact path={routes.lookbook.summary.path} render={() => <LookbookSummaryPage />} />

        <Route exact path={routes.reimbursement.create.path} component={ManageReimbursementPage} />

        <Route exact path={routes.discounts.root.path} component={DiscountsPage} />
        <Route exact path={routes.discounts.create.path} render={() => <ManageCodePage action="create" defaultPurpose="share" />} />
        <Route exact path={routes.discounts.edit.path} render={() => <ManageCodePage action="edit" />} />
        <Route exact path={routes.discounts.duplicate.path} render={() => <ManageCodePage action="duplicate" />} />


        <Route exact path={routes.billing.root.path} component={BillingPage} />

        {/*<Route exact path={routes.invoices.root.path} component={InvoicesPage} />
        <Route exact path={routes.invoices.summary.path} component={InvoicesSummaryPage} />*/}

        <Route exact path={routes.discover.root.path} component={DiscoverPage} />

        <Route exact path={routes.dataExports.path} component={DataExportsPage} />

        {/*<Route exact path={routes.paymentMethods.root.path} component={PaymentMethodsPage} />
        <Route exact path={routes.paymentMethods.add.path} component={PaymentMethodsAddPage} />*/}

        <Route exact path={routes.members.list.path} component={MembersPage} />
        <Route exact path={routes.members.invitations.path} component={InvitationsPage}/>
        <Route exact path={routes.members.invite.path} component={MemberInvitePage}/>

        <Route exact path={routes.organization.root.path} component={OrganizationPage} />
        <Route exact path={routes.organization.contactDetails.path} component={OrganizationEditContactPage}/>

        <Route exact path={routes.account.root.path} component={AccountPage} />
        <Route exact path={routes.account.nameEdit.path} component={AccountNameEditPage} />
        <Route exact path={routes.account.emailEdit.path} component={AccountEmailEditPage} />
        <Route exact path={routes.account.phoneEdit.path} component={AccountPhoneEditPage} />
        <Route exact path={routes.account.phoneVerify.path} component={AccountPhoneVerification} />
        
        <Route exact path={routes.chat.root.path} component={ChatPage} />

        <Route exact path={routes.performance.path} component={PerformancePage} />

        {/*<Route exact path={routes.tools.path} component={ToolsPage} />*/}
        <Route exact path={routes.help.path} component={HelpPage} />
        {/*<Route exact path={routes.notifications.root.path} component={NotificationsPage} />
        <Route exact path={routes.notifications.settings.path} component={NotificationsSettingsPage} />*/}
        <Route exact path={routes.SecretSettings.path} component={SecretSettingsPage} />
        <Route path="*" component={NotFound} />
      </Switch>
    </Suspense>
  );
};


export default Routes;
